/**
 * BookingWidget Component
 * 
 * Multi-step patient booking wizard for appointment scheduling.
 * Steps: SelectProvider → SelectType → SelectDate → SelectTime → Confirm → Success
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Loader2, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  CheckCircle2, 
  ArrowLeft, 
  ArrowRight,
  Video,
  Building2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/services/api';
import { cn } from '@/lib/utils';
import { format, parseISO, addMonths, startOfMonth } from 'date-fns';
import { PaymentStep } from '@/components/booking/public/PaymentStep';

interface Provider {
  id: string;
  first_name: string;
  last_name: string;
  credentials?: string;
  specialty?: string;
  photo_url?: string;
  bio?: string;
}

interface AppointmentType {
  id: string;
  name: string;
  description?: string;
  duration_minutes: number;
  price_cents?: number;
  color?: string;
  telehealth_enabled: boolean;
  in_person_enabled: boolean;
}

interface TimeSlot {
  start: string;
  end: string;
}

interface BookingWidgetProps {
  sessionToken: string;
  clinicId: string;
  clinicName: string;
  patientId: string;
  patientName?: string;
  preselectedProviderId?: string;
}

type Step = 'provider' | 'type' | 'date' | 'time' | 'confirm' | 'payment' | 'success';

const BookingWidget: React.FC<BookingWidgetProps> = ({
  sessionToken,
  clinicId,
  clinicName,
  patientId,
  patientName,
  preselectedProviderId,
}) => {
  const [step, setStep] = useState<Step>('provider');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Selection state
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);
  const [visitMode, setVisitMode] = useState<'telehealth' | 'in_person'>('telehealth');
  const [availableDays, setAvailableDays] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [holdId, setHoldId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [createdAppointment, setCreatedAppointment] = useState<any>(null);
  const [patientTimezone] = useState<string>(() => {
    // Auto-detect patient's timezone
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/Chicago';
    }
  });

  // API headers with session token
  const getHeaders = useCallback(() => ({
    'x-session-token': sessionToken,
  }), [sessionToken]);

  // Load providers on mount
  useEffect(() => {
    loadProviders();
  }, []);

  // If preselected provider, skip provider step
  useEffect(() => {
    if (preselectedProviderId && providers.length > 0) {
      const provider = providers.find(p => p.id === preselectedProviderId);
      if (provider) {
        setSelectedProvider(provider);
        setStep('type');
      }
    }
  }, [preselectedProviderId, providers]);

  // Load appointment types when provider is selected
  useEffect(() => {
    if (selectedProvider) {
      loadAppointmentTypes();
    }
  }, [selectedProvider]);

  // Load available days when type is selected and month changes
  useEffect(() => {
    if (selectedProvider && selectedType) {
      loadAvailableDays();
    }
  }, [selectedProvider, selectedType, currentMonth]);

  // Load time slots when date is selected
  useEffect(() => {
    if (selectedProvider && selectedDate) {
      loadTimeSlots();
    }
  }, [selectedProvider, selectedDate, selectedType]);

  // Clear time slots when appointment type changes (to ensure fresh slots with correct duration)
  useEffect(() => {
    setTimeSlots([]);
    setSelectedSlot(null);
  }, [selectedType]);

  const loadProviders = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/public/booking/providers', {
        headers: getHeaders(),
      });
      if (response.data.success) {
        setProviders(response.data.data.providers);
        // If only one provider, auto-select
        if (response.data.data.providers.length === 1) {
          setSelectedProvider(response.data.data.providers[0]);
          setStep('type');
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load providers. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAppointmentTypes = async () => {
    if (!selectedProvider) return;
    try {
      setIsLoading(true);
      const response = await apiClient.get(
        `/public/booking/providers/${selectedProvider.id}/appointment-types`,
        { headers: getHeaders() }
      );
      if (response.data.success) {
        setAppointmentTypes(response.data.data.appointmentTypes);
        // If only one type, auto-select
        if (response.data.data.appointmentTypes.length === 1) {
          setSelectedType(response.data.data.appointmentTypes[0]);
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load appointment types.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableDays = async () => {
    if (!selectedProvider) return;
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const response = await apiClient.get(
        `/public/booking/providers/${selectedProvider.id}/available-days`,
        { 
          params: { month, year },
          headers: getHeaders(),
        }
      );
      if (response.data.success) {
        setAvailableDays(response.data.data.availableDates);
      }
    } catch (error) {
      console.error('Failed to load available days:', error);
    }
  };

  const loadTimeSlots = async () => {
    if (!selectedProvider || !selectedDate) return;
    try {
      setIsLoading(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await apiClient.get(
        `/public/booking/providers/${selectedProvider.id}/slots`,
        { 
          params: { 
            date: dateStr,
            appointment_type_id: selectedType?.id,
            patient_timezone: patientTimezone, // Send patient's timezone for proper conversion
          },
          headers: getHeaders(),
        }
      );
      if (response.data.success) {
        setTimeSlots(response.data.data.slots);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load available time slots.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const holdTimeSlot = async (slot: TimeSlot) => {
    if (!selectedProvider) return;
    try {
      setIsLoading(true);
      const response = await apiClient.post(
        '/public/booking/hold-slot',
        {
          practitioner_id: selectedProvider.id,
          start_time: slot.start,
          end_time: slot.end,
        },
        { headers: getHeaders() }
      );
      if (response.data.success) {
        setHoldId(response.data.data.hold_id);
        setSelectedSlot(slot);
        setStep('confirm');
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast({
          title: 'Slot Unavailable',
          description: 'This time slot was just booked. Please select another.',
          variant: 'destructive',
        });
        loadTimeSlots(); // Refresh slots
      } else {
        toast({
          title: 'Error',
          description: 'Failed to hold time slot. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const confirmBooking = async (overridePaymentMethodId?: string) => {
    if (!selectedProvider || !selectedSlot || !selectedType) return;
    const pmId = overridePaymentMethodId ?? paymentMethodId;
    try {
      setIsLoading(true);
      const response = await apiClient.post(
        '/public/booking/book',
        {
          practitioner_id: selectedProvider.id,
          start_time: selectedSlot.start,
          end_time: selectedSlot.end,
          appointment_type_id: selectedType.id,
          visit_mode: visitMode,
          notes: notes || undefined,
          payment_method_id: pmId || undefined,
        },
        { 
          headers: {
            ...getHeaders(),
            'x-idempotency-key': `${patientId}-${selectedSlot.start}`,
          },
        }
      );
      if (response.data.success) {
        setCreatedAppointment(response.data.data.appointment);
        setStep('success');
        toast({
          title: 'Appointment Booked!',
          description: 'You will receive a confirmation email shortly.',
        });
      }
    } catch (error) {
      toast({
        title: 'Booking Failed',
        description: 'Unable to complete booking. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    switch (step) {
      case 'type':
        setStep('provider');
        break;
      case 'date':
        setStep('type');
        break;
      case 'time':
        setStep('date');
        break;
      case 'confirm':
        setStep('time');
        setHoldId(null);
        break;
      case 'payment':
        setStep('confirm');
        setPaymentMethodId(null);
        break;
    }
  };

  // Step renderers
  const renderProviderSelection = () => (
    <div className="space-y-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl">Select a Provider</CardTitle>
        <CardDescription>Choose the healthcare provider you'd like to see</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : (
          <RadioGroup
            value={selectedProvider?.id || ''}
            onValueChange={(value) => {
              const provider = providers.find(p => p.id === value);
              if (provider) setSelectedProvider(provider);
            }}
            className="space-y-3"
          >
            {providers.map(provider => (
              <div key={provider.id} className="flex items-center space-x-3">
                <RadioGroupItem value={provider.id} id={provider.id} />
                <Label 
                  htmlFor={provider.id} 
                  className="flex-1 cursor-pointer rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {provider.photo_url ? (
                      <img 
                        src={provider.photo_url} 
                        alt={provider.first_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">
                        {provider.first_name} {provider.last_name}
                        {provider.credentials && <span className="text-muted-foreground">, {provider.credentials}</span>}
                      </p>
                      {provider.specialty && (
                        <p className="text-sm text-muted-foreground">{provider.specialty}</p>
                      )}
                    </div>
                  </div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      </CardContent>
      <CardFooter className="pt-4">
        <Button 
          onClick={() => setStep('type')} 
          disabled={!selectedProvider}
          className="w-full"
        >
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardFooter>
    </div>
  );

  const renderTypeSelection = () => (
    <div className="space-y-4">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle className="text-xl">Select Appointment Type</CardTitle>
            <CardDescription>
              Booking with {selectedProvider?.first_name} {selectedProvider?.last_name}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : (
          <>
            <RadioGroup
              value={selectedType?.id || ''}
              onValueChange={(value) => {
                const type = appointmentTypes.find(t => t.id === value);
                if (type) {
                  setSelectedType(type);
                  // Set default visit mode based on type
                  if (type.telehealth_enabled && !type.in_person_enabled) {
                    setVisitMode('telehealth');
                  } else if (!type.telehealth_enabled && type.in_person_enabled) {
                    setVisitMode('in_person');
                  }
                }
              }}
              className="space-y-3"
            >
              {appointmentTypes.map(type => (
                <div key={type.id} className="flex items-center space-x-3">
                  <RadioGroupItem value={type.id} id={type.id} />
                  <Label 
                    htmlFor={type.id} 
                    className="flex-1 cursor-pointer rounded-lg border p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{type.name}</p>
                        {type.description && (
                          <p className="text-sm text-muted-foreground">{type.description}</p>
                        )}
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline">
                            <Clock className="w-3 h-3 mr-1" />
                            {type.duration_minutes} min
                          </Badge>
                          {type.telehealth_enabled && (
                            <Badge variant="secondary">
                              <Video className="w-3 h-3 mr-1" />
                              Telehealth
                            </Badge>
                          )}
                          {type.in_person_enabled && (
                            <Badge variant="secondary">
                              <Building2 className="w-3 h-3 mr-1" />
                              In-Person
                            </Badge>
                          )}
                        </div>
                      </div>
                      {type.price_cents !== null && type.price_cents !== undefined && (
                        <span className="font-semibold text-primary">
                          ${(type.price_cents / 100).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Visit mode selection */}
            {selectedType && selectedType.telehealth_enabled && selectedType.in_person_enabled && (
              <div className="mt-4 p-4 border rounded-lg">
                <Label className="text-sm font-medium mb-3 block">Visit Type</Label>
                <RadioGroup
                  value={visitMode}
                  onValueChange={(value) => setVisitMode(value as 'telehealth' | 'in_person')}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="telehealth" id="telehealth" />
                    <Label htmlFor="telehealth" className="flex items-center cursor-pointer">
                      <Video className="w-4 h-4 mr-2" />
                      Video Visit
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="in_person" id="in_person" />
                    <Label htmlFor="in_person" className="flex items-center cursor-pointer">
                      <Building2 className="w-4 h-4 mr-2" />
                      In-Person
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </>
        )}
      </CardContent>
      <CardFooter className="pt-4">
        <Button 
          onClick={() => setStep('date')} 
          disabled={!selectedType}
          className="w-full"
        >
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardFooter>
    </div>
  );

  const renderDateSelection = () => {
    const isDateAvailable = (date: Date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      return availableDays.includes(dateStr);
    };

    return (
      <div className="space-y-4">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <CardTitle className="text-xl">Select a Date</CardTitle>
              <CardDescription>
                {selectedType?.name} with {selectedProvider?.first_name}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex justify-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(date) => {
              setSelectedDate(date);
              if (date) setStep('time');
            }}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today || !isDateAvailable(date);
            }}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            className="rounded-md border"
          />
        </CardContent>
      </div>
    );
  };

  const renderTimeSelection = () => (
    <div className="space-y-4">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle className="text-xl">Select a Time</CardTitle>
            <CardDescription>
              {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : timeSlots.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No available times for this date.</p>
            <Button variant="link" onClick={() => setStep('date')}>
              Select a different date
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-80 overflow-y-auto">
            {timeSlots.map((slot, idx) => (
              <Button
                key={idx}
                variant="outline"
                className="h-10"
                onClick={() => holdTimeSlot(slot)}
                disabled={isLoading}
              >
                {format(parseISO(slot.start), 'h:mm a')}
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </div>
  );

  const renderConfirmation = () => (
    <div className="space-y-4">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle className="text-xl">Confirm Your Appointment</CardTitle>
            <CardDescription>Review the details below</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-muted/50 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Provider</p>
              <p className="font-medium">
                {selectedProvider?.first_name} {selectedProvider?.last_name}
                {selectedProvider?.credentials && `, ${selectedProvider?.credentials}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Date & Time</p>
              <p className="font-medium">
                {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
                {selectedSlot && ` at ${format(parseISO(selectedSlot.start), 'h:mm a')}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Appointment Type</p>
              <p className="font-medium">{selectedType?.name} ({selectedType?.duration_minutes} min)</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {visitMode === 'telehealth' ? (
              <Video className="w-5 h-5 text-muted-foreground" />
            ) : (
              <Building2 className="w-5 h-5 text-muted-foreground" />
            )}
            <div>
              <p className="text-sm text-muted-foreground">Visit Type</p>
              <p className="font-medium">
                {visitMode === 'telehealth' ? 'Video Visit' : 'In-Person Visit'}
              </p>
            </div>
          </div>
        </div>

        <div>
          <Label htmlFor="notes">Notes (optional)</Label>
          <Textarea
            id="notes"
            placeholder="Any additional information for your provider..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1"
            rows={3}
          />
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button
          onClick={() => {
            if (selectedType?.price_cents && selectedType.price_cents > 0) {
              setStep('payment');
            } else {
              confirmBooking();
            }
          }}
          disabled={isLoading}
          className="w-full"
          size="lg"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Booking...
            </>
          ) : (
            <>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {selectedType?.price_cents && selectedType.price_cents > 0
                ? `Continue to Payment ($${(selectedType.price_cents / 100).toFixed(2)})`
                : 'Confirm Appointment'}
            </>
          )}
        </Button>
        <p className="text-xs text-center text-muted-foreground">
          You will receive a confirmation email with calendar invite
        </p>
      </CardFooter>
    </div>
  );

  const renderPayment = () => (
    <div className="space-y-4">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <CardTitle className="text-xl">Payment</CardTitle>
            <CardDescription>Complete payment to book your appointment</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <PaymentStep
          sessionToken={sessionToken}
          priceCents={selectedType?.price_cents || 0}
          stripeAccountId=''
          flow='magic-link'
          onComplete={(pmId) => {
            setPaymentMethodId(pmId);
            confirmBooking(pmId);
          }}
          onSkip={() => {
            confirmBooking('');
          }}
        />
      </CardContent>
    </div>
  );

  const renderSuccess = () => (
    <div className="space-y-4 text-center py-8">
      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle2 className="w-8 h-8 text-green-600" />
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-2xl text-green-600">Appointment Booked!</CardTitle>
        <CardDescription>Your appointment has been confirmed</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-muted/50 rounded-lg p-4 text-left space-y-2">
          <p className="font-medium">
            {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
            {selectedSlot && ` at ${format(parseISO(selectedSlot.start), 'h:mm a')}`}
          </p>
          <p className="text-sm text-muted-foreground">
            {selectedType?.name} with {selectedProvider?.first_name} {selectedProvider?.last_name}
          </p>
          {visitMode === 'telehealth' && (
            <Badge variant="secondary">
              <Video className="w-3 h-3 mr-1" />
              Video Visit - Link will be sent before appointment
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          A confirmation email with calendar invite has been sent to you.
        </p>
      </CardContent>
    </div>
  );

  // Progress indicator
  const getStepIndex = () => {
    const steps: Step[] = ['provider', 'type', 'date', 'time', 'confirm', 'payment', 'success'];
    return steps.indexOf(step);
  };
  const totalVisibleSteps = selectedType?.price_cents && selectedType.price_cents > 0 ? 6 : 5;

  return (
    <Card className="w-full max-w-lg mx-auto">
      {/* Progress bar */}
      {step !== 'success' && (
        <div className="px-6 pt-6">
          <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
            <span>Step {Math.min(getStepIndex() + 1, totalVisibleSteps)} of {totalVisibleSteps}</span>
            <span>{clinicName}</span>
          </div>
          <div className="h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${(Math.min(getStepIndex() + 1, totalVisibleSteps) / totalVisibleSteps) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Step content */}
      {step === 'provider' && renderProviderSelection()}
      {step === 'type' && renderTypeSelection()}
      {step === 'date' && renderDateSelection()}
      {step === 'time' && renderTimeSelection()}
      {step === 'confirm' && renderConfirmation()}
      {step === 'payment' && renderPayment()}
      {step === 'success' && renderSuccess()}
    </Card>
  );
};

export default BookingWidget;








